import type { UseMutationReturnType } from '@tanstack/vue-query'
import type { Enums } from '@xleddyl/types'
import { computed, ref, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import type Form from '~/components/core/Form.vue'
import type { FormFields } from '~/components/core/Form.vue'

type RbacScope = Enums<'ORGANIZATION_RBAC_SCOPE'>

export const toNullable = <T>(value: T | '' | null | undefined) => value || null

type MutationLike<TArgs, TResult = unknown> = Pick<
   UseMutationReturnType<TResult, unknown, TArgs, unknown>,
   'mutateAsync' | 'isPending'
>

type ButtonOverrides = {
   confirmText?: MaybeRefOrGetter<string>
   confirmIcon?: string
   cancelText?: MaybeRefOrGetter<string>
   cancelIcon?: string
}

export type UseEntityModalOptions<
   T extends FormFields,
   TSaveArgs,
   TSaveResult = unknown,
   TRemoveArgs = never,
> = {
   open: Ref<boolean | undefined>
   isEdit: MaybeRefOrGetter<boolean>
   scopes: {
      create: RbacScope[]
      update: RbacScope[]
      delete?: RbacScope[]
   }
   fields: (formDisabled: ComputedRef<boolean>) => T
   save: MutationLike<TSaveArgs, TSaveResult>
   remove?: MutationLike<TRemoveArgs>
   toSavePayload: (data: T) => TSaveArgs
   toRemovePayload?: () => TRemoveArgs
   onSaved?: (result: TSaveResult) => void | Promise<void>
   closeOnSave?: boolean
   buttons?: ButtonOverrides
}

export const useEntityModal = <
   T extends FormFields,
   TSaveArgs,
   TSaveResult = unknown,
   TRemoveArgs = never,
>(
   options: UseEntityModalOptions<T, TSaveArgs, TSaveResult, TRemoveArgs>
) => {
   const { t } = useI18n()
   const rbac = useRbac()

   const formRef = ref<InstanceType<typeof Form>>()
   const dirty = ref(false)
   const submitting = ref(false)
   const removing = ref(false)

   const formDisabled = computed(() => {
      return !rbac(toValue(options.isEdit) ? options.scopes.update : options.scopes.create)
   })

   const actionDisabled = computed(() => formDisabled.value || !dirty.value)

   const formData = useFormData(() => options.fields(formDisabled), { resetOn: options.open })

   const submit = async (data: T) => {
      if (submitting.value || options.save.isPending.value) return
      submitting.value = true
      const { data: result, error } = await tryCatch(async () => {
         return await options.save.mutateAsync(options.toSavePayload(data))
      })
      submitting.value = false
      if (error) return
      await options.onSaved?.(result as TSaveResult)
      if (options.closeOnSave !== false) options.open.value = false
   }

   const remove = async () => {
      if (!options.remove || !options.toRemovePayload) return
      if (removing.value || options.remove.isPending.value) return
      removing.value = true
      const { error } = await tryCatch(async () => {
         await options.remove!.mutateAsync(options.toRemovePayload!())
      })
      removing.value = false
      if (error) return
      options.open.value = false
   }

   const confirmButton = computed(() => ({
      text:
         toValue(options.buttons?.confirmText) ||
         (toValue(options.isEdit) ? t('common.save') : t('common.create')),
      icon: options.buttons?.confirmIcon,
      action: () => formRef.value?.submit(),
      loading: submitting.value || options.save.isPending.value,
      disabled: actionDisabled.value,
   }))

   const cancelButton = computed(() => {
      if (!options.remove || !toValue(options.isEdit)) return undefined
      return {
         text: toValue(options.buttons?.cancelText) || t('common.delete'),
         icon: options.buttons?.cancelIcon || 'trash',
         action: remove,
         loading: removing.value || options.remove.isPending.value,
         disabled: options.scopes.delete ? !rbac(options.scopes.delete) : false,
      }
   })

   return { formData, formRef, dirty, formDisabled, confirmButton, cancelButton, submit, remove }
}
